import { useCallback, useEffect, useState } from "react";
import {
  detectPwaAlreadyInstalled,
  isInstalledPwa,
  isIosDevice,
  isMobileDevice,
  markPwaInstalled,
} from "../utils/pwaUtils";

function isStandaloneMode() {
  return isInstalledPwa();
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(isStandaloneMode);
  const [isIos, setIsIos] = useState(false);
  const [alreadyInstalled, setAlreadyInstalled] = useState(isStandaloneMode);

  useEffect(() => {
    let active = true;

    const standalone = isStandaloneMode();
    setIsStandalone(standalone);
    setIsIos(isIosDevice() && !standalone);

    if (standalone) {
      markPwaInstalled();
      setAlreadyInstalled(true);
    } else {
      detectPwaAlreadyInstalled().then((installed) => {
        if (active) setAlreadyInstalled(installed);
      });
    }

    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const onDisplayModeChange = () => {
      const nowStandalone = isStandaloneMode();
      setIsStandalone(nowStandalone);
      if (nowStandalone) {
        markPwaInstalled();
        setAlreadyInstalled(true);
      }
    };

    const onAppInstalled = () => {
      markPwaInstalled();
      setAlreadyInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onAppInstalled);
    window.matchMedia("(display-mode: standalone)").addEventListener("change", onDisplayModeChange);

    return () => {
      active = false;
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onAppInstalled);
      window.matchMedia("(display-mode: standalone)").removeEventListener("change", onDisplayModeChange);
    };
  }, []);

  const canInstall =
    isMobileDevice() &&
    !isStandalone &&
    !alreadyInstalled &&
    Boolean(deferredPrompt || isIos);

  const install = useCallback(async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        markPwaInstalled();
        setAlreadyInstalled(true);
        setDeferredPrompt(null);
      }
      return outcome === "accepted";
    }
    return false;
  }, [deferredPrompt]);

  return {
    canInstall,
    install,
    isIos,
    hasNativePrompt: Boolean(deferredPrompt),
  };
}
